// The workbench's Playlists button in the top bar. It GOES to playlists; the
// header's Add to playlist icon beside Tune details is the one place that
// FILES the tune on screen (Oct 2026, user: "we have two playlist buttons…
// neither of them actually take you to the playlist"). It used to open the
// same Add to panel as that icon, from when the phone header had no add
// button of its own; it has carried one since, so the two duplicated each
// other and a tap on a playlist's name added the tune instead of opening it.
//
// Rows open the playlist (the drawer's Playlists list, from the workbench),
// the one you are working through marked; then New playlist (the index's own
// naming form, as the drawer hands off) and All playlists. Signed out there
// are no playlists, so only All playlists, whose page asks you to sign in.

import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ListMusic, Plus } from 'lucide-react';
import { usePlaylists, usePlaylistsAvailable } from '@/data/playlistAccess';
import { APP_ROUTES } from '@/navigation';
import { useStore } from '@/state/store';
import { resolveSetCursor } from '@/storage/playlists';
import { Popover } from './Popover';

export function PlaylistsMenu() {
  const available = usePlaylistsAvailable();
  const playlists = usePlaylists();
  const setCursor = useStore((s) => s.setCursor);
  const inSet = useMemo(() => resolveSetCursor(playlists, setCursor), [playlists, setCursor]);
  const navigate = useNavigate();

  return (
    <Popover
      label={
        <span className="tune-action-label">
          <ListMusic
            className="tune-browser-icon"
            size={18}
            strokeWidth={1.8}
            aria-hidden="true"
          />
          <span className="tune-browser-label">Playlists</span>
        </span>
      }
      title="Playlists"
      testid="playlists-menu"
      align="right"
      overTransport
    >
      {(close) => (
        <div className="playlists-menu" data-testid="playlists-menu-panel">
          {available && playlists.length > 0 && (
            <nav className="playlists-menu-list" aria-label="Playlists">
              {playlists.map((p) => {
                const current = inSet?.list.id === p.id;
                return (
                  <Link
                    key={p.id}
                    className={`add-menu-item playlists-menu-item${current ? ' is-current' : ''}`}
                    to={`${APP_ROUTES.playlists}/${p.id}`}
                    onClick={close}
                    aria-current={current ? 'true' : undefined}
                    data-testid="playlists-menu-playlist"
                  >
                    <span className="playlists-menu-name">{p.name}</span>
                    {current && (
                      <span className="add-menu-recent" data-testid="playlists-menu-current">
                        current
                      </span>
                    )}
                    <span className="muted" aria-hidden="true">
                      {p.entries.length}
                    </span>
                    <span className="visually-hidden">
                      {p.entries.length} {p.entries.length === 1 ? 'tune' : 'tunes'}
                    </span>
                  </Link>
                );
              })}
            </nav>
          )}
          {available && (
            <button
              type="button"
              className="add-menu-item playlists-menu-new"
              data-testid="playlists-menu-new"
              onClick={() => {
                close();
                navigate(APP_ROUTES.playlists, { state: { create: true } });
              }}
            >
              <Plus size={15} aria-hidden="true" /> New playlist
            </button>
          )}
          <Link
            className="playlists-menu-all"
            to={APP_ROUTES.playlists}
            onClick={close}
            data-testid="playlists-menu-all"
          >
            All playlists
          </Link>
        </div>
      )}
    </Popover>
  );
}
